<template>
  <div>
    <v-card>
      <v-card-title>
        <span class="headline">
          {{ isEditMode ? 'Edit Rate' : 'Create New Rate' }}
        </span>
        <v-spacer></v-spacer>
        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form
          id="rate-form"
          ref="form"
          v-model="valid"
          @submit.prevent="save"
        >
          <!-- Cost Category and Rate Type Row -->
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.costCategory"
                :items="costCategoryOptions"
                label="Cost Category *"
                :rules="costCategoryRules"
                :disabled="isEditMode"
                no-data-text="Not available"
                outlined
                :hint="costCategoryHint"
                persistent-hint
              >
                <template v-slot:selection="{ item }">
                  <v-chip
                    :color="getCategoryColor(item.value || item)"
                    text-color="white"
                    small
                  >
                    <v-icon left small>
                      {{ getCategoryIcon(item.value || item) }}
                    </v-icon>
                    {{ item.text || item }}
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-chip
                        :color="getCategoryColor(item.value)"
                        text-color="white"
                        small
                      >
                        <v-icon left small>{{ getCategoryIcon(item.value) }}</v-icon>
                        {{ item.text }}
                      </v-chip>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.rateType"
                :items="rateTypes"
                label="Rate Type *"
                :rules="rateTypeRules"
                outlined
                :hint="rateTypeHint"
                persistent-hint
              >
                <template v-slot:item="{ item }">
                  <v-list-item-icon>
                    <v-icon :color="getRateTypeColor(item.value)">
                      {{ getRateTypeIcon(item.value) }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- Rate Value Row -->
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                :value="rateInput"
                :label="getRateLabel()"
                type="text"
                inputmode="decimal"
                :rules="rateRules"
                :prefix="ratePrefix"
                :suffix="rateSuffix"
                outlined
                :hint="getRateHint()"
                persistent-hint
                @input="handleRateInput"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch
                v-model="form.isActive"
                label="Active Rate"
                :hint="activeRateHint"
                persistent-hint
                class="mt-2"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Date Range Row -->
          <v-row>
            <v-col cols="12" md="6">
              <v-menu
                v-model="effectiveFromMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.effectiveFrom"
                    label="Effective From *"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    :rules="effectiveFromRules"
                    hint="The date when this rate becomes effective"
                    persistent-hint
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.effectiveFrom"
                  @input="effectiveFromMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="effectiveToMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.effectiveTo"
                    label="Effective To *"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    :rules="effectiveToRules"
                    hint="The date when this rate expires"
                    persistent-hint
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.effectiveTo"
                  @input="effectiveToMenu = false"
                  :min="form.effectiveFrom"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Created By Row (for edit mode) -->
          <v-row v-if="isEditMode">
            <v-col cols="12" md="6">
              <v-text-field
                :value="form.createdBy"
                label="Created By"
                readonly
                outlined
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="formatDateTime(form.createdAt)"
                label="Created At"
                readonly
                outlined
                disabled
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Overlap Warning -->
          <v-row v-if="overlapWarning">
            <v-col cols="12">
              <v-alert
                type="warning"
                dense
                outlined
              >
                <v-icon slot="prepend">mdi-alert</v-icon>
                <strong>Date Range Overlap Warning:</strong>
                {{ overlapWarning }}
              </v-alert>
            </v-col>
          </v-row>

          <!-- Rate Preview -->
          <v-row v-if="form.costCategory && form.rate && form.rateType">
            <v-col cols="12">
              <v-card outlined>
                <v-card-subtitle>Rate Preview</v-card-subtitle>
                <v-card-text>
                  <div class="text-h6">
                    {{ formatCostCategory(form.costCategory) }}:
                    <span class="primary--text">{{ formatRate(form.rate, form.rateType) }}</span>
                  </div>
                  <div class="text-body-2 grey--text">
                    Effective from {{ formatDate(form.effectiveFrom) }}
                    to {{ formatDate(form.effectiveTo) }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          type="submit"
          form="rate-form"
          color="primary"
          :disabled="!valid || saving"
          :loading="saving"
        >
          {{ isEditMode ? 'Update Rate' : 'Create Rate' }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog
      v-model="confirmDialog"
      max-width="500px"
      @keydown.enter="saveConfirmed"
    >
      <v-card>
        <v-card-title class="headline">
          {{ isEditMode ? 'Confirm Rate Update' : 'Confirm Rate Creation' }}
        </v-card-title>
        <v-card-text>
          <div class="mb-3">
            You are about to {{ isEditMode ? 'update' : 'create' }} the following rate:
          </div>
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Cost Category</v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatCostCategory(form.costCategory) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Rate</v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatRate(form.rate, form.rateType) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>Effective Period</v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatDate(form.effectiveFrom) }} to {{ formatDate(form.effectiveTo) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-alert
            v-if="overlapWarning"
            type="warning"
            dense
            class="mt-3"
          >
            {{ overlapWarning }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            @click="saveConfirmed"
          >
            {{ isEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { ValidationUtils } from '../../types';
import costCategoryMixin from '../../mixins/costCategoryMixin';
import appConfig from '../../config/appConfig';

export default {
  name: 'RateForm',
  mixins: [costCategoryMixin],
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      valid: false,
      saving: false,
      confirmDialog: false,
      effectiveFromMenu: false,
      effectiveToMenu: false,
      overlapWarning: null,
      rateInput: '',
      form: {
        costCategory: '',
        rate: null,
        rateType: '',
        effectiveFrom: '',
        effectiveTo: '',
        isActive: true,
        createdBy: appConfig.getParentUserName(),
        createdAt: null,
      },
    };
  },
  computed: {
    ...mapGetters('rateMaster', ['allRates']),

    isEditMode() {
      return !!this.id;
    },

    costCategoryHint() {
      return this.isEditMode
        ? 'Cost category cannot be changed when editing'
        : 'Select the type of charge this rate applies to';
    },

    rateTypeHint() {
      return 'Fixed rates are absolute amounts, percentage rates are applied to base amounts';
    },

    ratePrefix() {
      return ['FIXED', 'DAILY'].includes(this.form.rateType) ? this.$currencyCode : '';
    },

    rateSuffix() {
      if (this.form.rateType === 'PERCENTAGE') {
        return '%';
      }
      return this.form.rateType === 'DAILY' ? '/day' : '';
    },

    activeRateHint() {
      return this.form.isActive
        ? 'This rate is active and can be used in calculations'
        : 'This rate is inactive and will not be used';
    },

    currentUserName() {
      return appConfig.getParentUserName();
    },

    costCategoryRules() {
      return [
        (v) => !!v || 'Cost Category is required',
      ];
    },

    rateTypeRules() {
      return [
        (v) => !!v || 'Rate Type is required',
      ];
    },

    rateRules() {
      return [
        (v) => (v !== null && v !== '') || 'Rate is required',
        (v) => !Number.isNaN(this.parseFormattedNumber(v)) || 'Rate must be a valid number',
        (v) => this.parseFormattedNumber(v) >= 0 || 'Rate must be non-negative',
        (v) => {
          if (this.form.rateType === 'PERCENTAGE' && this.parseFormattedNumber(v) > 100) {
            return 'Percentage rate cannot exceed 100%';
          }
          return true;
        },
      ];
    },

    effectiveFromRules() {
      return [
        (v) => !!v || 'Effective From date is required',
      ];
    },

    effectiveToRules() {
      return [
        (v) => !!v || 'Effective To date is required',
        (v) => {
          if (v && this.form.effectiveFrom) {
            const fromDate = new Date(this.form.effectiveFrom);
            const toDate = new Date(v);
            if (toDate <= fromDate) {
              return 'Effective To date must be after Effective From date';
            }
          }
          return true;
        },
      ];
    },

    rateTypes() {
      return [
        {
          text: 'Fixed Amount',
          value: 'FIXED',
          description: `Absolute monetary amount (e.g., 150 ${
            this.$currencyCode
          } per kWh)`,
        },
        {
          text: 'Percentage',
          value: 'PERCENTAGE',
          description: 'Percentage of base amount (e.g., 18% VAT)',
        },
        {
          text: 'Daily Rate',
          value: 'DAILY',
          description: `Daily rate multiplied by billing period days (e.g., 16.44 ${
            this.$currencyCode
          } per day)`,
        },
      ];
    },
  },
  watch: {
    // Check for overlaps when dates or category change
    'form.costCategory': 'checkForOverlaps',
    'form.effectiveFrom': 'checkForOverlaps',
    'form.effectiveTo': 'checkForOverlaps',
  },
  async created() {
    if (this.isEditMode) {
      await this.loadExistingRate();
    } else {
      this.form.createdBy = this.currentUserName;
      // Set default effective dates for new rates
      const today = new Date();
      const nextYear = new Date(
        today.getFullYear() + 1,
        today.getMonth(),
        today.getDate(),
      );

      this.form.effectiveFrom = this.formatDateForInput(today);
      this.form.effectiveTo = this.formatDateForInput(nextYear);
    }
  },
  methods: {
    ...mapActions('rateMaster', ['createRate', 'updateRate', 'getRateById']),

    async loadExistingRate() {
      try {
        const rate = await this.getRateById(this.id);
        if (rate) {
          const createdBy = rate.createdBy === 'current_user'
            ? this.currentUserName
            : rate.createdBy;

          this.form = {
            ...rate,
            createdBy,
            effectiveFrom: this.formatDateForInput(rate.effectiveFrom),
            effectiveTo: this.formatDateForInput(rate.effectiveTo),
          };
          this.rateInput = this.formatNumberInput(rate.rate);
        } else {
          this.$store.dispatch('showSnackbar', {
            message: 'Rate not found',
            color: 'error',
          });
          this.cancel();
        }
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: `Error loading rate: ${error.message}`,
          color: 'error',
        });
        this.cancel();
      }
    },

    checkForOverlaps() {
      this.overlapWarning = null;

      if (!this.form.costCategory || !this.form.effectiveFrom || !this.form.effectiveTo) {
        return;
      }

      const categoryRates = this.allRates.filter((rate) => (
        rate.costCategory === this.form.costCategory
        && rate.isActive
        && ((!this.isEditMode) || (rate.id !== this.id))
      ));

      const newRange = {
        startDate: new Date(this.form.effectiveFrom),
        endDate: new Date(this.form.effectiveTo),
      };

      const overlappingRates = categoryRates.filter((rate) => {
        const existingRange = {
          startDate: new Date(rate.effectiveFrom),
          endDate: new Date(rate.effectiveTo),
        };
        return ValidationUtils.doDateRangesOverlap(newRange, existingRange);
      });

      if (overlappingRates.length > 0) {
        const ratesList = overlappingRates.map((rate) => (
          `${this.formatDate(rate.effectiveFrom)} to ${this.formatDate(rate.effectiveTo)}`
        )).join(', ');

        this.overlapWarning = `This date range overlaps with existing rates: ${ratesList}`;
      }
    },

    getRateTypeColor(rateType) {
      if (rateType === 'FIXED') {
        return 'blue';
      }
      return rateType === 'PERCENTAGE' ? 'green' : 'orange';
    },

    getRateTypeIcon(rateType) {
      if (rateType === 'FIXED') {
        return 'mdi-currency-usd';
      }
      return rateType === 'PERCENTAGE' ? 'mdi-percent' : 'mdi-calendar-clock';
    },

    parseFormattedNumber(value) {
      if (value === null || value === undefined || value === '') {
        return NaN;
      }
      return Number(String(value).replace(/,/g, ''));
    },

    formatNumberInput(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }

      const normalizedValue = String(value).replace(/,/g, '');
      const [integerPart, decimalPart] = normalizedValue.split('.');
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      if (decimalPart !== undefined) {
        return `${formattedInteger}.${decimalPart}`;
      }
      return formattedInteger;
    },

    formatDateForInput(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return '';
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    handleRateInput(value) {
      const rawValue = String(value || '').replace(/,/g, '');
      const sanitizedValue = rawValue
        .replace(/[^\d.]/g, '')
        .replace(/(\..*)\./g, '$1');

      this.rateInput = this.formatNumberInput(sanitizedValue);
      this.form.rate = sanitizedValue === '' ? null : this.parseFormattedNumber(sanitizedValue);
    },

    save() {
      if (this.$refs.form.validate()) {
        this.confirmDialog = true;
      }
    },

    async saveConfirmed() {
      if (this.saving) return;

      this.saving = true;
      try {
        const rateData = {
          ...this.form,
          createdBy: this.form.createdBy || this.currentUserName,
          rate: this.parseFormattedNumber(this.rateInput),
        };

        if (this.isEditMode) {
          await this.updateRate({ id: this.id, ...rateData });
          this.$store.dispatch('showSnackbar', {
            message: 'Rate updated successfully',
            color: 'success',
          });
        } else {
          await this.createRate(rateData);
          this.$store.dispatch('showSnackbar', {
            message: 'Rate created successfully',
            color: 'success',
          });
        }

        this.confirmDialog = false;
        this.$router.push('/master-data');
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: `Error saving rate: ${error.message}`,
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.$router.push('/master-data');
    },

    getRateLabel() {
      if (this.form.rateType === 'PERCENTAGE') {
        return 'Percentage Rate *';
      } if (this.form.rateType === 'DAILY') {
        return 'Daily Rate Amount *';
      }
      return 'Fixed Rate Amount *';
    },

    getRateHint() {
      if (this.form.rateType === 'PERCENTAGE') {
        return 'Enter percentage value (e.g., 18 for 18%)';
      } if (this.form.rateType === 'DAILY') {
        return `Enter daily rate amount in ${this.$currencySymbol} (will be multiplied by billing period days)`;
      }
      return `Enter fixed amount in ${this.$currencySymbol}`;
    },

    formatRate(rate, rateType) {
      if (!rate) return '';

      if (rateType === 'PERCENTAGE') {
        return `${rate}%`;
      } if (rateType === 'DAILY') {
        return `${this.$formatCurrency(rate)}/day`;
      }
      return this.$formatCurrency(rate);
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>
